import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ShieldCheck, FolderLock } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { StatusBadge, money, shortDate } from "../components/common";
import { fileUrl } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

export default function ProofPackPublic() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-pack", token],
    queryFn: async () => (await api.get(`/proof-packs/public/${token}`)).data,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3.5">
          <Logo />
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-success"><ShieldCheck className="h-4 w-4" />Verified proof</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-12">
        {isLoading ? (
          <div className="space-y-4"><Skeleton className="h-12 w-64" />{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
        ) : isError || !data ? (
          <div className="grid place-items-center py-24 text-center">
            <FolderLock className="mb-4 h-12 w-12 text-muted-foreground" />
            <h1 className="font-heading text-2xl font-bold">Proof Pack not found</h1>
            <p className="text-muted-foreground">This link may have expired or been deleted.</p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">Proof Pack</p>
              <h1 className="font-heading text-4xl font-black tracking-tight">{data.pack.name}</h1>
              <p className="mt-2 text-muted-foreground">
                {data.pack.count} receipt(s) · {money(data.pack.total_value)} · shared by {data.owner_name || "a ProofPass user"}
              </p>
            </div>
            <div className="space-y-4">
              {data.receipts.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`public-receipt-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {r.store_logo_path && <img src={fileUrl(r.store_logo_path)} alt="" className="h-6 w-6 rounded object-cover" />}
                        <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{r.store_name}</p>
                      </div>
                      <p className="mt-1 font-heading text-lg font-extrabold">{r.items?.map((i) => i.name).join(", ")}</p>
                      <p className="text-sm text-muted-foreground">{shortDate(r.purchase_date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-xl font-black">{money(r.total)}</p>
                      {r.warranty_status && <div className="mt-1"><StatusBadge status={r.warranty_status} /></div>}
                    </div>
                  </div>
                  {r.warranty_expiry && <p className="mt-3 text-xs text-muted-foreground">Warranty until {shortDate(r.warranty_expiry)}</p>}
                </div>
              ))}
            </div>
            <p className="mt-10 text-center text-sm text-muted-foreground">Secured by ProofPass · Digital proof of purchase</p>
          </>
        )}
      </main>
    </div>
  );
}
